import { useState, useMemo } from "react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calculator, Copy, RotateCcw, Scale, Clock, Banknote } from "lucide-react";
import { HAFTRECHNER } from "@/lib/constants";

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const fmtHE = (n) =>
  (isFinite(n) ? n : 0).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 2 });
const fmtGeld = (n) => (isFinite(n) ? n : 0).toLocaleString("de-DE", { maximumFractionDigits: 0 });
const fmtDT = (d) =>
  isNaN(d)
    ? "—"
    : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const initialState = () => ({
  geldstrafe: "",
  haftzeitHE: "",
  anwalt: "Nein",
  kooperativ: "Kooperativ",
  vorstrafen: "beachtet",
  begruendung: "Kein Vorsatz",
  zahlungsart: "Verweigert die Zahlung",
  haftbeginn: nowLocal(),
});

const GreenSelect = ({ testid, value, onChange, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="bg-emerald-500/10 border-emerald-500/40 rounded-sm text-zinc-100 focus:ring-emerald-500 h-10">
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="bg-[#0C0E12] border-zinc-700">
      {options.map((o) => (
        <SelectItem key={o} value={o} className="text-zinc-200">
          {o}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function Haftrechner() {
  const [s, setS] = useState(initialState());
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const calc = useMemo(() => {
    const geld = parseFloat(String(s.geldstrafe).replace(",", ".")) || 0;
    const manualHE = parseFloat(String(s.haftzeitHE).replace(",", ".")) || 0;
    const anwaltJa = s.anwalt === "Ja";
    const verweigert = s.zahlungsart !== "Zahlt";
    const geldEffektiv = anwaltJa ? geld * 0.8 : geld;
    const geldHE = verweigert ? geldEffektiv / HAFTRECHNER.RATE_GELD_PRO_HE : 0;
    const totalHE = manualHE + geldHE;
    const minuten = Math.round(totalHE * HAFTRECHNER.MIN_PRO_HE);
    const beginn = new Date(s.haftbeginn);
    const ende = isNaN(beginn) ? new Date(NaN) : new Date(beginn.getTime() + minuten * 60000);
    return { geld, manualHE, anwaltJa, verweigert, geldEffektiv, geldHE, totalHE, minuten, beginn, ende };
  }, [s]);

  const kurztext = useMemo(() => {
    const { verweigert, geldEffektiv, geld, geldHE, manualHE, totalHE, beginn, ende, minuten, anwaltJa } = calc;
    const geldPart = verweigert
      ? `${fmtGeld(geldEffektiv)}$ (${fmtHE(geldHE)} HE)`
      : `${fmtGeld(geldEffektiv)}$ gezahlt`;
    const anwaltPart = anwaltJa ? `Anwalt -20% (${fmtGeld(geldEffektiv)}$) | ` : "";
    return `${s.kooperativ} | Vorstrafen ${s.vorstrafen} | ${s.begruendung} | ${anwaltPart}${s.zahlungsart}: ${geldPart} + ${fmtHE(manualHE)} HE → ${fmtHE(totalHE)} HE | ${fmtDT(beginn)} → ${fmtDT(ende)} | ${minuten} Minuten`;
  }, [calc, s.kooperativ, s.vorstrafen, s.begruendung, s.zahlungsart]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(kurztext);
      toast.success("Kurztext kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const reset = () => {
    setS(initialState());
    toast.success("Werte zurückgesetzt");
  };

  const fieldRow = (label, node) => (
    <div className="flex items-center gap-3">
      <Label className="w-40 shrink-0 text-sm text-zinc-300">{label}</Label>
      <div className="flex-1">{node}</div>
    </div>
  );

  return (
    <div>
      <PageHeader
        eyebrow="SADCR · Haft"
        title="Haftrechner"
        description="Umrechner für Geldstrafe in Haftzeit (HE) inkl. automatischem Kurztext."
        icon={Calculator}
      />
      <div className="p-8 space-y-6">
        <div className="flex justify-end">
          <Button data-testid="haftrechner-reset" onClick={reset} variant="outline" className="rounded-sm border-zinc-700 gap-2 h-9">
            <RotateCcw className="h-4 w-4" /> Werte zurücksetzen
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Eingaben */}
          <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Banknote className="h-4 w-4 text-emerald-400" />
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Angaben zum Strafmaß</h2>
            </div>

            {fieldRow("Geldstrafe ($)",
              <Input
                data-testid="haftrechner-geldstrafe"
                inputMode="decimal"
                value={s.geldstrafe}
                onChange={(e) => set("geldstrafe", e.target.value)}
                placeholder="z. B. 5000"
                className="bg-emerald-500/10 border-emerald-500/40 rounded-sm focus-visible:ring-emerald-500"
              />
            )}
            {fieldRow("Haftzeit (HE)",
              <Input
                data-testid="haftrechner-haftzeit"
                inputMode="decimal"
                value={s.haftzeitHE}
                onChange={(e) => set("haftzeitHE", e.target.value)}
                placeholder="Grund-HE, z. B. 2"
                className="bg-emerald-500/10 border-emerald-500/40 rounded-sm focus-visible:ring-emerald-500"
              />
            )}
            {fieldRow("Anwalt", <GreenSelect testid="haftrechner-anwalt" value={s.anwalt} onChange={(v) => set("anwalt", v)} options={HAFTRECHNER.ANWALT} />)}
            {fieldRow("Kooperativ", <GreenSelect testid="haftrechner-kooperativ" value={s.kooperativ} onChange={(v) => set("kooperativ", v)} options={HAFTRECHNER.KOOPERATIV} />)}
            {fieldRow("Vorstrafen", <GreenSelect testid="haftrechner-vorstrafen" value={s.vorstrafen} onChange={(v) => set("vorstrafen", v)} options={HAFTRECHNER.VORSTRAFEN} />)}
            {fieldRow("Begründung zur Tat", <GreenSelect testid="haftrechner-begruendung" value={s.begruendung} onChange={(v) => set("begruendung", v)} options={HAFTRECHNER.BEGRUENDUNG} />)}
            {fieldRow("Zahlungs Art", <GreenSelect testid="haftrechner-zahlungsart" value={s.zahlungsart} onChange={(v) => set("zahlungsart", v)} options={HAFTRECHNER.ZAHLUNGSART} />)}
            {fieldRow("Haftbeginn",
              <Input
                data-testid="haftrechner-haftbeginn"
                type="datetime-local"
                value={s.haftbeginn}
                onChange={(e) => set("haftbeginn", e.target.value)}
                className="bg-emerald-500/10 border-emerald-500/40 rounded-sm focus-visible:ring-emerald-500 [color-scheme:dark]"
              />
            )}
          </div>

          {/* Ergebnis */}
          <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Scale className="h-4 w-4 text-primary" />
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                {calc.anwaltJa ? "Umrechnung (Anwalt −20 %)" : "Normale Umrechnung"}
              </h2>
            </div>

            <div className="space-y-2 font-mono text-sm">
              <div className="flex justify-between border-b border-zinc-800/60 pb-2">
                <span className="text-zinc-500">{calc.anwaltJa ? "Geldstrafe − 20 %" : "Geldstrafe"}</span>
                <span data-testid="haftrechner-geld-effektiv" className="text-zinc-200">{fmtGeld(calc.geldEffektiv)} $</span>
              </div>
              <div className="flex justify-between border-b border-zinc-800/60 pb-2">
                <span className="text-zinc-500">Geld → Haftstrafe</span>
                <span data-testid="haftrechner-geld-he" className="text-zinc-200">{fmtHE(calc.geldHE)} HE {calc.verweigert ? "" : "(bezahlt)"}</span>
              </div>
              <div className="flex justify-between border-b border-zinc-800/60 pb-2">
                <span className="text-zinc-500">Grund-Haftzeit</span>
                <span className="text-zinc-200">{fmtHE(calc.manualHE)} HE</span>
              </div>
            </div>

            <div className="rounded-sm border border-primary/40 bg-primary/10 p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Gesamte Haftzeit</div>
              <div data-testid="haftrechner-total-he" className="mt-1 font-mono text-3xl font-black text-primary leading-none">{fmtHE(calc.totalHE)} HE</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-sm border border-zinc-800 p-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Haftende</div>
                <div data-testid="haftrechner-haftende" className="mt-1 font-mono text-sm text-zinc-200">{fmtDT(calc.ende)}</div>
              </div>
              <div className="rounded-sm border border-zinc-800 p-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Dauer</div>
                <div data-testid="haftrechner-minuten" className="mt-1 font-mono text-sm text-zinc-200">{calc.minuten} Minuten</div>
              </div>
            </div>
          </div>
        </div>

        {/* Kurztext */}
        <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] p-5 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Bemerkung / Summe zur ausgewählten Strafe</h2>
            <Button data-testid="haftrechner-copy" onClick={copy} className="rounded-sm bg-primary hover:bg-blue-600 text-white gap-2 h-8">
              <Copy className="h-4 w-4" /> Kopieren
            </Button>
          </div>
          <div data-testid="haftrechner-kurztext" className="rounded-sm border border-zinc-800 bg-black/30 p-4 font-mono text-sm text-zinc-200 break-words">
            {kurztext}
          </div>
        </div>
      </div>
    </div>
  );
}
